// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Implement all custom behaviours for elements which should be available/
 * applied globally. This includes:
 * - Applying polyfill for custom web components to ensure cross-browser
 * compatibility.
 * - Cloning template elements.
 * - Fetching page elements by accessor IDs.
 * Polyfill and define all custom web components required for app.
 * @author James Reid
 */

// @ts-check

// @@imports-dependencies
import "@ungap/custom-elements"
import { createElement, icons } from "lucide"

// @@imports-package
import * as CUSTOM_COMPONENTS from "../../components/index.js"

// @@body
// Define module-scoped global variables.
let CUSTOM_ELEMENTS_DEFINED = false

/**
 * Define custom elements required by application with protection against
 * repeated execution.
 * @param {{name:string, element:any}[]} elements Array of objects containing
 * custom components required for the current specific view/path or context.
 */
const defineCustomElements = (elements = []) => {
    // Ignore call if CUSTOM_ELEMENTS_DEFINED flag is set, as defining custom
    // elements twice will throw an error.
    if (CUSTOM_ELEMENTS_DEFINED) { return }

    // Define additional custom elements passed in arguments.
    for (const { name, element } of elements) {
        customElements.define(name, element)
    }

    // Define all custom elements exported from the components directory using
    // the kebab-case version of the class name as the custom element name.
    for (const [, component] of Object.entries(CUSTOM_COMPONENTS)) {
        customElements.define(component.customName, component)
    }

    // Set CUSTOM_ELEMENTS_DEFINED flag.
    CUSTOM_ELEMENTS_DEFINED = true
}

/**
 * Clone template element, returning first element child.
 * @template {HTMLElement} ElementPrototype
 * @param {string} id ID of template element to clone.
 * @param {object} obj Optional destructured parameters object.
 * @param {boolean} [obj.renderIcons] Should lucide icons be rendered to cloned
 * element before returning?
 * @param {ElementPrototype} [obj.prototype] Prototype of expected element type.
 * If not set, any result extending HTMLElement may be returned.
 * @returns {ElementPrototype} First child of cloned template element.
 * @throws {Error} Throws when:
 * - Template not found.
 * - Element found is not a template element.
 * - Template is empty.
 * - Template contents does not match the expected HTML element.
 */
const cloneTemplate = (id, { renderIcons = false, prototype } = {}) => {
    // Fetch template element, throwing if not found or if not a template.
    const template = document.getElementById(id)
    if (!template) { throw new Error(`Template by id ${id} not found`) }
    if (!(template instanceof HTMLTemplateElement)) {
        throw new Error(`Element by id ${id} is not a template`)
    }

    // Clone template, throwing if template is empty or if first element child
    // does not match expected.
    const node = template.content.cloneNode(true)
    const element = /** @type {Document} */ (node).body ||
    /** @type {Element} */ (node).firstElementChild
    if (!element) {
        throw new Error(`Template by id ${id} is empty`)
    }
    if (!(element instanceof (prototype?.constructor || HTMLElement))) {
        throw new Error(`Template found by id ${id} does not match prototype ${
            prototype?.toString() || HTMLElement.prototype.toString()
        }`)
    }

    // If required, render lucide icons within first element child of cloned
    // template.
    if (renderIcons) {
        element.querySelectorAll("i").forEach(icon => {
            const iconName = icon.dataset.lucide
            if (iconName) {
                const iconAccessor = iconName.split("").reduce((acc, cur) => {
                    if (cur === "-") { return { ...acc, isCapital: true } }

                    const nextChar = acc.isCapital
                        ? cur.toUpperCase()
                        : cur.toLowerCase()

                    return { ...acc, string: `${acc.string}${nextChar}` }
                }, { string: "", isCapital: true }).string

                try {
                    // @ts-ignore
                    const info = createElement(icons[iconAccessor])
                    icon.replaceWith(info)
                }
                catch { throw new Error("Lucide icon not resolved") }
            }
        })
    }

    return /** @type {ElementPrototype} */ (element)
}

/**
 * Get an element within the document or a parent element by the value of its
 * data-accessor attribute and optional element type checking.
 * @template {HTMLElement} ElementPrototype
 * @param {string} accessor Value of data-accessor attribute to query.
 * @param {object} obj Optional destructured parameters object.
 * @param {HTMLElement} [obj.parent] Parent element to query.
 * @param {ElementPrototype} [obj.prototype] Prototype of expected element type.
 * If not set, any result extending HTMLElement may be returned.
 * @returns {ElementPrototype} Element if found matching data-accessor attribute
 * and expected element type.
 * @throws {Error} Throws when:
 * - Element with matching data-accessor attribute is not found.
 * - Element found does not match expected element type.
 */
const getElementByAccessor = (accessor, { parent, prototype } = {}) => {
    // Fetch element by value of data-accessor attribute passed in arguments.
    const element = (parent || document)
        .querySelector(`[data-accessor=${accessor}]`)

    // Throw if element not found or if element type does not match expected.
    if (!element) {
        throw new Error(`Element not found by accessor '${accessor}'`)
    }
    if (!(element instanceof (prototype?.constructor || HTMLElement))) {
        throw new Error(`Element found does not match prototype ${
            prototype?.toString() || HTMLElement.prototype.toString()
        }`)
    }

    return /** @type {ElementPrototype} */ (element)
}

// @@exports
export { defineCustomElements, cloneTemplate, getElementByAccessor }
